import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useApi } from '../../hooks/useApi';
import { useAppState } from '../../context/AppStateContext';
import GradeForm from '../../components/GradeForm';
import {
    ChevronLeftIcon,
    ArrowDownTrayIcon,
    ChatBubbleBottomCenterTextIcon,
    DocumentTextIcon,
    Squares2X2Icon,
    ListBulletIcon,
    MagnifyingGlassIcon,
    XMarkIcon
} from '@heroicons/react/24/outline';
import { generateSubmissionPDF } from '../../utils/pdfGenerator';
import { getLevelData } from '../../utils/cbcUtils';

const SubmissionListPage = () => {
    const { assignmentId } = useParams();
    const navigate = useNavigate();
    const { get } = useApi();
    const { showToast } = useAppState();
    const location = useLocation();
    const isClub = new URLSearchParams(location.search).get('type') === 'club';

    const [assignment, setAssignment] = useState(null);
    const [submissions, setSubmissions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedSubmission, setSelectedSubmission] = useState(null);
    const [previewSubmission, setPreviewSubmission] = useState(null);
    const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'table'
    const [searchQuery, setSearchQuery] = useState('');

    const fetchData = useCallback(async () => {
        try {
            setLoading(true);
            const assignmentUrl = isClub
                ? `/api/events/club-assignments/${assignmentId}/?t=${Date.now()}`
                : `/api/assignments/${assignmentId}/?t=${Date.now()}`;
            const submissionsUrl = isClub
                ? `/api/events/club-assignments/${assignmentId}/submissions/?t=${Date.now()}`
                : `/api/assignments/${assignmentId}/submissions/?t=${Date.now()}`;

            const [assignmentData, submissionsData] = await Promise.all([
                get(assignmentUrl),
                get(submissionsUrl)
            ]);
            setAssignment(assignmentData);
            setSubmissions(submissionsData || []);
        } catch (error) {
            console.error('Error fetching submission data:', error);
            showToast('Failed to load submissions', 'error');
        } finally {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [assignmentId]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const handleDownload = (submission, assignment) => {
        if (submission.file && submission.file.toLowerCase().endsWith('.pdf') && submission.file_url) {
            const link = document.createElement('a');
            link.href = submission.file_url;
            link.target = "_blank";
            link.download = `Submission_${submission.student_name}_${assignment.title}.pdf`.replace(/[^a-z0-9]/gi, '_');
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } else {
            generateSubmissionPDF(submission, assignment);
        }
    };

    const renderGradeBand = (submission) => {
        const isGraded = submission.status?.toLowerCase() === 'graded' || submission.grade !== null || !!submission.competency_level;
        if (!isGraded) return <span className="text-[10px] text-slate-300 italic font-bold uppercase tracking-widest">Pending</span>;

        const rawScore = submission.grade !== null ? Math.round(submission.grade) : 0;
        const totalMarks = assignment?.total_marks ?? '?';
        const levelCode = submission.competency_level || 'ME';
        const levelData = getLevelData(levelCode);

        return (
            <div className="flex flex-col items-center">
                <span className="text-lg font-black text-[#18216D] mb-1">
                    {rawScore}
                    <span className="text-xs text-slate-400 font-bold"> / {totalMarks}</span>
                </span>
                <div className={`px-3 py-1 rounded-lg border ${levelData.bg} ${levelData.border} transition-all`}>
                    <span className={`text-xs font-black uppercase tracking-widest ${levelData.color}`}>
                        {levelCode}
                    </span>
                </div>
            </div>
        );
    };


    if (loading) {
        return (
            <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center">
                <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#18216D]/10 border-t-[#18216D] mb-4"></div>
                <div className="text-[#18216D] font-black uppercase tracking-widest text-[10px]">Retrieving Submissions...</div>
            </div>
        );
    }

    if (!assignment) return null;

    const filteredSubmissions = submissions.filter(s =>
        s.student_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        s.student_id?.toString().includes(searchQuery)
    );

    const gradedCount = submissions.filter(s =>
        s.status?.toLowerCase() === 'graded' ||
        (s.grade !== null && s.grade !== undefined) ||
        !!s.competency_level
    ).length;
    const pendingCount = submissions.length - gradedCount;

    return (
        <div className="min-h-screen bg-slate-50 pb-20">
            {/* High-Density Header */}
            <div className="bg-white border-b border-gray-100 sticky top-0 z-30 shadow-sm">
                <div className="max-w-7xl mx-auto px-6 py-4">
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
                        <div className="flex items-center gap-4">
                            <button
                                onClick={() => {
                                    if (isClub) {
                                        navigate('/teacher/clubs');
                                    } else if (assignment?.learning_area) {
                                        navigate(`/teacher/courses/${assignment.learning_area}?tab=assignments`);
                                    } else {
                                        navigate(-1);
                                    }
                                }}
                                className="p-2 hover:bg-slate-50 rounded-xl text-slate-400 hover:text-[#18216D] transition-all"
                            >
                                <ChevronLeftIcon className="w-5 h-5" />
                            </button>
                            <div>
                                <div className="flex items-center gap-3">
                                    <h1 className="text-xl font-black text-[#18216D] tracking-tight">{assignment.title}</h1>
                                    <span className="px-2 py-0.5 bg-[#FFC425]/10 text-[#18216D] text-[9px] font-black uppercase tracking-widest rounded-lg border border-[#FFC425]/20">
                                        {assignment.learning_area_name || 'Mathematics'}
                                    </span>
                                </div>
                                <p className="text-slate-400 font-bold text-[9px] uppercase tracking-[0.1em] mt-0.5 flex items-center">
                                    <DocumentTextIcon className="w-3 h-3 mr-1.5" />
                                    {assignment.strand_name} • {assignment.sub_strand_name}
                                </p>
                            </div>
                        </div>

                        {/* Search & View Switcher */}
                        <div className="flex-1 max-w-md mx-8 hidden lg:block">
                            <div className="relative">
                                <MagnifyingGlassIcon className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-slate-300" />
                                <input
                                    type="text"
                                    placeholder="Quick search scholars..."
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    className="w-full pl-10 pr-4 py-3 bg-slate-50 border-none rounded-2xl text-xs font-bold text-[#18216D] focus:ring-4 focus:ring-[#18216D]/5 transition-all"
                                />
                            </div>
                        </div>

                        <div className="flex items-center gap-4">
                            <div className="flex bg-slate-100 p-1 rounded-xl">
                                <button
                                    onClick={() => setViewMode('grid')}
                                    className={`p-2 rounded-lg transition-all ${viewMode === 'grid' ? 'bg-white text-[#18216D] shadow-sm' : 'text-slate-400 hover:text-slate-600'}`}
                                >
                                    <Squares2X2Icon className="w-4 h-4" />
                                </button>
                                <button
                                    onClick={() => setViewMode('table')}
                                    className={`p-2 rounded-lg transition-all ${viewMode === 'table' ? 'bg-white text-[#18216D] shadow-sm' : 'text-slate-400 hover:text-slate-600'}`}
                                >
                                    <ListBulletIcon className="w-4 h-4" />
                                </button>
                            </div>

                            {/* Summary Stats */}
                            <div className="hidden sm:flex items-center gap-2">
                                <div className="bg-emerald-50 px-3 py-1.5 rounded-xl border border-emerald-100/50">
                                    <span className="text-[10px] font-black text-emerald-600 uppercase tracking-widest">{gradedCount} Graded</span>
                                </div>
                                <div className="bg-[#FFC425]/10 px-3 py-1.5 rounded-xl border border-[#FFC425]/20">
                                    <span className="text-[10px] font-black text-[#18216D] uppercase tracking-widest">{pendingCount} Waiting</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Task Description (Restored) */}
                    {assignment.description && (
                        <div className="mt-4 pt-4 border-t border-slate-50">
                            <p className="text-xs font-medium text-slate-500 line-clamp-2 italic">
                                <span className="text-[10px] font-black uppercase tracking-widest text-slate-300 mr-2 not-italic">Task Brief:</span>
                                {assignment.description}
                            </p>
                        </div>
                    )}
                </div>
            </div>

            {/* Submissions View */}
            <main className="max-w-7xl mx-auto px-6 mt-12">
                {submissions.length === 0 ? (
                    <div className="text-center py-20 bg-white rounded-[3rem] border border-slate-100 shadow-xl shadow-indigo-900/5">
                        <div className="w-20 h-20 bg-slate-50 rounded-[1.5rem] flex items-center justify-center mx-auto mb-6">
                            <ChatBubbleBottomCenterTextIcon className="w-10 h-10 text-slate-200" />
                        </div>
                        <p className="text-slate-400 font-bold uppercase tracking-widest text-xs">No submissions received yet</p>
                    </div>
                ) : viewMode === 'grid' ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {filteredSubmissions.map((submission) => (
                            <div
                                key={submission.id}
                                className="bg-white rounded-[2rem] border border-slate-100 p-8 hover:shadow-2xl hover:shadow-indigo-900/10 transition-all group relative overflow-hidden"
                            >
                                <div className="absolute top-0 right-0 w-24 h-24 bg-slate-50 rounded-bl-full -mr-12 -mt-12 transition-transform group-hover:scale-110"></div>

                                <div className="relative z-10">
                                        <div className="flex items-center gap-4 mb-6">
                                            <div className="w-14 h-14 bg-[#18216D] text-white rounded-2xl flex items-center justify-center font-black text-lg shadow-lg shadow-indigo-900/20">
                                                {submission.student_name?.charAt(0).toUpperCase()}
                                            </div>
                                            <div className="flex-1">
                                                <div className="flex items-center justify-between">
                                                    <div>
                                                        <h4 className="font-black text-[#18216D] tracking-tight">{submission.student_name}</h4>
                                                        <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mt-0.5">
                                                            ID: {submission.student_id || '24/00863'}
                                                        </p>
                                                    </div>
                                                    {submission.status?.toLowerCase() === 'graded' && (
                                                        <div className="scale-90 origin-right">
                                                            {renderGradeBand(submission)}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>

                                    <div className="space-y-4 mb-8">
                                        <div className="flex items-center justify-between text-xs font-bold">
                                            <span className="text-slate-400 uppercase tracking-widest">Status</span>
                                            {submission.grade || submission.competency_level || submission.status?.toLowerCase() === 'graded' ? (
                                                <span className="px-3 py-1 bg-emerald-50 text-emerald-600 rounded-full border border-emerald-100 uppercase tracking-widest text-[9px] font-black">Graded</span>
                                            ) : (
                                                <span className="px-3 py-1 bg-amber-50 text-amber-600 rounded-full border border-amber-100 uppercase tracking-widest text-[9px] font-black">Waiting</span>
                                            )}
                                        </div>
                                        <div className="flex items-center justify-between text-xs font-bold">
                                            <span className="text-slate-400 uppercase tracking-widest">Submitted</span>
                                            <span className="text-[#18216D]">{new Date(submission.submitted_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                                        </div>
                                    </div>

                                    <div className="flex gap-2">
                                        {!assignment.is_manual && (
                                            <button
                                                onClick={() => handleDownload(submission, assignment)}
                                                className="p-2.5 bg-slate-50 text-[#18216D] rounded-xl flex items-center justify-center gap-2 hover:bg-[#18216D] hover:text-white transition-all shadow-sm"
                                                title="Export PDF Receipt"
                                            >
                                                <ArrowDownTrayIcon className="w-4 h-4" />
                                            </button>
                                        )}
                                        {(submission.text_response || 
                                          (submission.file && ['.pdf', '.docx', '.doc'].some(ext => submission.file.toLowerCase().endsWith(ext)))) && (
                                            <button
                                                onClick={() => navigate(`/teacher/assignments/${assignmentId}/mark/${submission.id}`)}
                                                className="flex-[2] py-4 bg-[#18216D] text-white rounded-2xl flex items-center justify-center gap-2 hover:bg-[#0D164F] transition-all font-black text-[10px] uppercase tracking-widest shadow-lg shadow-indigo-900/20 mt-4 w-full"
                                            >
                                                {submission.grade || submission.competency_level || submission.status?.toLowerCase() === 'graded' ? 'Review Outcome' : 'Grade Work'}
                                            </button>
                                        )}

                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="bg-white rounded-[2rem] border border-slate-100 overflow-hidden shadow-xl shadow-indigo-900/5">
                        <table className="w-full text-left">
                            <thead className="bg-slate-50 border-b border-slate-100">
                                <tr>
                                    <th className="px-8 py-5 text-[9px] font-black text-slate-400 uppercase tracking-widest">Scholar</th>
                                    <th className="px-8 py-5 text-[9px] font-black text-slate-400 uppercase tracking-widest">Student ID</th>
                                    <th className="px-8 py-5 text-[9px] font-black text-slate-400 uppercase tracking-widest">Submitted At</th>
                                    <th className="px-8 py-5 text-[9px] font-black text-slate-400 uppercase tracking-widest">Grade & Band</th>
                                    <th className="px-8 py-5 text-[9px] font-black text-slate-400 uppercase tracking-widest">Status</th>
                                    <th className="px-8 py-5 text-[9px] font-black text-slate-400 uppercase tracking-widest text-right">Actions</th>

                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-50">
                                {filteredSubmissions.map((submission) => (
                                    <tr key={submission.id} className="hover:bg-slate-50/50 transition-colors">
                                        <td className="px-8 py-6">
                                            <div className="flex items-center gap-4">
                                                <div className="w-10 h-10 bg-[#18216D] text-white rounded-xl flex items-center justify-center font-black text-xs">
                                                    {submission.student_name?.charAt(0).toUpperCase()}
                                                </div>
                                                <span className="font-bold text-[#18216D] text-sm">{submission.student_name}</span>
                                            </div>
                                        </td>
                                        <td className="px-8 py-6 text-sm font-bold text-slate-500">{submission.student_id || '24/00863'}</td>
                                        <td className="px-8 py-6 text-xs font-bold text-[#18216D]">
                                            {new Date(submission.submitted_at).toLocaleString([], { dateStyle: 'short', timeStyle: 'short' })}
                                        </td>
                                        <td className="px-8 py-6">
                                            {renderGradeBand(submission)}
                                        </td>
                                        <td className="px-8 py-6">

                                            {submission.grade || submission.competency_level || submission.status?.toLowerCase() === 'graded' ? (
                                                <span className="px-3 py-1 bg-emerald-50 text-emerald-600 rounded-full text-[9px] font-black uppercase tracking-widest border border-emerald-100">Graded</span>
                                            ) : (
                                                <span className="px-3 py-1 bg-amber-50 text-amber-600 rounded-full text-[9px] font-black uppercase tracking-widest border border-amber-100">Waiting</span>
                                            )}
                                        </td>
                                        <td className="px-8 py-6 text-right">
                                            <div className="flex items-center justify-end gap-3 transition-all">
                                                {!assignment.is_manual && (
                                                    <button
                                                        onClick={() => handleDownload(submission, assignment)}
                                                        className="p-2 bg-slate-100 text-[#18216D] rounded-lg hover:bg-[#18216D] hover:text-white transition-all shadow-sm"
                                                        title="Export PDF Receipt"
                                                    >
                                                        <ArrowDownTrayIcon className="w-4 h-4" />
                                                    </button>
                                                )}
                                                {(submission.text_response || 
                                                  (submission.file && ['.pdf', '.docx', '.doc'].some(ext => submission.file.toLowerCase().endsWith(ext)))) && (
                                                    <button
                                                        onClick={() => navigate(`/teacher/assignments/${assignmentId}/mark/${submission.id}`)}
                                                        className="px-5 py-2.5 bg-[#18216D] text-white rounded-xl font-black text-[9px] uppercase tracking-widest hover:bg-[#0D164F] transition-all"
                                                    >
                                                        {submission.grade || submission.competency_level || submission.status?.toLowerCase() === 'graded' ? 'Review' : 'Grade'}
                                                    </button>
                                                )}

                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </main>

            {/* Grading Hub (Redesigned) */}
            {selectedSubmission && (
                <div className="fixed inset-0 bg-[#18216D]/60 backdrop-blur-md flex items-center justify-center z-[100] p-4 animate-in fade-in duration-300">
                    <div className="bg-white rounded-[3rem] shadow-2xl max-w-5xl w-full max-h-[95vh] overflow-hidden flex flex-col border border-white/20">
                        <GradeForm
                            assignmentId={assignmentId}
                            studentId={selectedSubmission.student_id}
                            onClose={() => setSelectedSubmission(null)}
                            onSuccess={() => {
                                fetchData();
                                setSelectedSubmission(null);
                            }}
                            isClub={isClub}
                        />
                    </div>
                </div>
            )}
            {/* Dedicated Submission Preview Modal */}
            {previewSubmission && (
                <div className="fixed inset-0 bg-[#0F172A]/80 backdrop-blur-xl flex items-center justify-center z-[150] p-4 animate-in fade-in duration-300">
                    <div className="bg-white rounded-[3rem] shadow-2xl max-w-5xl w-full max-h-[90vh] overflow-hidden flex flex-col border border-white/20">
                        {/* Header */}
                        <div className="px-10 py-3 border-b border-slate-50 flex items-center justify-between bg-white">
                            <div className="flex items-center gap-3">
                                <div className="w-8 h-8 bg-amber-50 rounded-lg flex items-center justify-center shadow-sm">
                                    <DocumentTextIcon className="w-4 h-4 text-amber-600" />
                                </div>
                                <div>
                                    <h3 className="text-base font-black text-[#18216D] tracking-tight">{previewSubmission.student_name}</h3>
                                    <p className="text-[9px] font-black text-slate-300 uppercase tracking-widest">Quick Preview</p>
                                </div>
                            </div>
                            <button
                                onClick={() => setPreviewSubmission(null)}
                                className="p-2 hover:bg-red-50 hover:text-red-500 rounded-xl text-slate-300 transition-all"
                            >
                                <XMarkIcon className="w-5 h-5" />
                            </button>
                        </div>

                        {/* Content Area */}
                        <div className="flex-1 overflow-y-auto p-6 custom-scrollbar bg-slate-50/30">
                            <div className="bg-white rounded-[1.5rem] p-8 shadow-xl shadow-indigo-900/5 border border-slate-100">
                                <p className="text-lg leading-[1.8] text-slate-600 font-medium whitespace-pre-wrap selection:bg-[#18216D] selection:text-white">
                                    {previewSubmission.text_response}
                                </p>
                            </div>
                        </div>

                        {/* Footer */}
                        <div className="px-10 py-3 border-t border-slate-100 bg-white flex items-center justify-between">
                            <p className="text-[9px] font-bold text-slate-400 italic">
                                Scrolled to end. Use 'Evaluate Work' for assessment.
                            </p>
                            <button
                                onClick={() => {
                                    const sub = previewSubmission;
                                    setPreviewSubmission(null);
                                    navigate(`/teacher/assignments/${assignmentId}/mark/${sub.id}`);
                                }}
                                className="px-6 py-2.5 bg-[#18216D] text-white rounded-xl font-black text-[10px] uppercase tracking-widest hover:bg-[#0D164F] transition-all shadow-lg"
                            >
                                Launch Marking Canvas →
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div >
    );
};

export default SubmissionListPage;
